import React from 'react';
import type { InlineLink } from '@/data/shared-types';
import { TextLink } from './TextLink';

interface LinkedTextProps {
  text: string;
  link: InlineLink;
  /**
   * Renders the unlinked text on either side of the anchor. Article pages pass
   * `CitedText`, so citation markers before or after the link keep resolving
   * against the page's `CitationProvider`.
   */
  renderText?: (part: string) => React.ReactNode;
}

const plain = (part: string): React.ReactNode => part;

/**
 * Prose with one phrase linked in place, at the first verbatim occurrence of
 * `link.label`. The anchor carries no arrow: it sits mid-sentence, and the
 * arrow (with the space the link reserves for it) would split the sentence
 * from its punctuation. A label the text does not contain follows the text
 * instead, the way the homepage FAQ renders a link that is not part of an
 * answer's own wording.
 */
export function LinkedText({ text, link, renderText = plain }: LinkedTextProps): React.ReactElement {
  const anchor = (
    <TextLink href={link.href} arrow="none">
      {link.label}
    </TextLink>
  );
  const index = text.indexOf(link.label);
  if (index === -1) {
    return (
      <>
        {renderText(text)} {anchor}
      </>
    );
  }
  const before = text.slice(0, index);
  const after = text.slice(index + link.label.length);
  return (
    <>
      {before && renderText(before)}
      {anchor}
      {after && renderText(after)}
    </>
  );
}
